import getPublicRuntimeConfig from 'utils/publicRuntimeConfig';
import type { MicrositeCustomTheme } from 'types/MicrositeTheme';
import type {
  ThemeWithAssets,
  ThemeWithoutAssets,
} from './generateReservationToken';

/**
 * Constructs absolute URLs for theme assets using current origin
 * @param themeName - The theme name (must match /public/themes/ folder)
 * @returns Object containing logo and favicon URLs
 */
export function buildThemeAssetUrls(themeName: string): {
  logoUrl: string;
  faviconUrls: {
    '32x32': string;
    '192x192': string;
    '180x180': string;
  };
} {
  // Get current origin from browser API
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  // Get basePath from Next.js config
  const publicRuntimeConfig = getPublicRuntimeConfig();
  const { basePath } = publicRuntimeConfig;

  // Construct provider app base (where static assets are served)
  const themePath = `${origin}${basePath}/themes/${themeName}`;

  return {
    logoUrl: `${themePath}/logo.svg`,
    faviconUrls: {
      '32x32': `${themePath}/favicon/site-icon-32x32.png`,
      '192x192': `${themePath}/favicon/site-icon-192x192.png`,
      '180x180': `${themePath}/favicon/site-icon-180x180.png`,
    },
  };
}

/**
 * Builds the themeWithAssets payload for the moving app reservation token.
 * When includeAssets is false (Embed sites), returns a slim payload with
 * only the colors needed for rendering — no logo or favicon URLs since the host
 * page controls those.
 */
export function buildThemeWithAssets({
  customTheme,
  includeAssets,
}: {
  customTheme: MicrositeCustomTheme;
  includeAssets: boolean;
}): ThemeWithAssets | ThemeWithoutAssets {
  if (!includeAssets) {
    return {
      theme: {
        colors: {
          background: customTheme.colors.background,
          progressBar: customTheme.colors.progressBar,
        },
        baseColors: customTheme.baseColors,
      },
    };
  }
  return {
    theme: customTheme,
    ...buildThemeAssetUrls(customTheme.name),
  };
}
